import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, PLATFORM_ID } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { Inject } from '@angular/core';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent {
  activeSection: string = '';
  currentRoute = '';
  isScrolled = false;

  showWorkWithUs = false;
  submitted = false;

  contactForm!: FormGroup;
  workForm!: FormGroup;

  showGoToTop = false;

  constructor(private fb: FormBuilder, private el: ElementRef, @Inject(PLATFORM_ID) private platformId: Object) { }

  ngOnInit() {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      company: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      country: [''],
      subject: [''],
      message: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(300)]],
    });

    this.workForm = this.fb.group({
      fullName: ['', Validators.required],
      organization: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      country: [''],
      website: [''],
      sector: [''],
      stage: [''],
      description: [''],
      distribution: [false],
      investment: [false],
      govtAccess: [false],
      jointDev: [false],
      file: [null],
    });
  }
  // Listen to window scroll
  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.showGoToTop = window.scrollY > 300; // show button after scrolling 300px
  }

  // Scroll to top smoothly
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  // convenience getters
  get fContact() {
    return this.contactForm.controls;
  }
  get fWork() {
    return this.workForm.controls;
  }

  toggleForm(type: 'contact' | 'work') {
    this.submitted = false; // reset errors when switching
    this.showWorkWithUs = type === 'work';
  }

  onSubmit() {
    this.submitted = true;
    const form = this.showWorkWithUs ? this.workForm : this.contactForm;

    if (form.invalid) {
      return;
    }

    console.log('Form submitted:', form.value);
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const logo = this.el.nativeElement.querySelector('.logo-animate');
      if (logo && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                logo.classList.add('opacity-100', 'scale-100');
                logo.classList.remove('opacity-0', 'scale-75');
              }
            });
          },
          { threshold: 0.5 }
        );
        observer.observe(logo);
      }
    }
  }

  sectors = [
    { icon: '🛰️', title: 'SpaceTech & Satellite Constellations', description: 'Satellite launches, constellations, and ground stations for global connectivity.' },
    { icon: '⚡', title: 'Next-Gen Energy & Batteries', description: 'Advanced batteries, EV infrastructure, and hydrogen power fueling the clean energy transition.' },
    { icon: '🌞', title: 'Solar & Clean Energy (CleanTech)', description: 'High-efficiency solar, waste-to-energy, and advanced recycling systems.' },
    { icon: '🌊⚡', title: 'Osmotic Power Systems (Blue Energy)', description: 'Turning river–sea flows into 24/7 clean electricity.' },
    { icon: '🏭', title: 'Digital Twin & Industry 4.0', description: 'IoT-driven models for factories, cities, and logistics — enabling smarter operations.' },
    { icon: '🧬', title: 'Bio-integrated Electronics & MedTech', description: 'Wearables and implantable diagnostics transforming healthcare and wellness.' },
    { icon: '💻', title: 'Quantum & Next-Gen Computing', description: 'Quantum systems and neuromorphic chips shaping the future of AI, defense, and finance.' },
    { icon: '🚗', title: 'Advanced Mobility', description: 'The future of transportation is autonomous, electric, and on-demand.' },
    { icon: '🛡️', title: 'Dual-Use Defense Technologies', description: 'AI surveillance, UAVs, and cybersecurity solutions for defense and enterprise.' },
  ];


  getLinkClasses(section: string): string {
    const isActive = this.activeSection === section;

    if (this.currentRoute === '/' || this.currentRoute === '/home') {
      return isActive
        ? 'text-[#E17A17]' // active color
        : 'text-[#1D4BA6] hover:text-[#E17A17]';
    }

    if (this.currentRoute === '/home-two') {
      if (this.isScrolled) {
        return isActive
          ? 'text-[#E17A17]'
          : 'text-[#1D4BA6] hover:text-[#ffffff]';
      } else {
        return isActive
          ? 'text-[#E17A17]'
          : 'text-white hover:text-[#ffffff]';
      }
    }

    // fallback
    return isActive
      ? 'text-[#E17A17]'
      : 'text-[#1D4BA6] hover:text-[#ffffff]';
  }

}
